import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { authApi, AuthAPIError } from '@/lib/auth-api';
import { Loader2, AlertCircle, CheckCircle2, ArrowLeft } from 'lucide-react';

interface ForgotPasswordFormProps {
	onBack: () => void;
}

export function ForgotPasswordForm({ onBack }: ForgotPasswordFormProps) {
	const { t } = useTranslation();
	const [email, setEmail] = useState('');
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [success, setSuccess] = useState(false);

	const handleSubmit = useCallback(
		async (e: React.FormEvent) => {
			e.preventDefault();
			setError(null);
			setIsLoading(true);

			try {
				const response = await authApi.forgotPassword({ email });

				if (response.success) {
					setSuccess(true);
				} else {
					setError(response.message || t('Odoslanie zlyhalo'));
				}
			} catch (err) {
				if (err instanceof AuthAPIError) {
					setError(err.message);
				} else {
					setError(t('Nastala neočakávaná chyba'));
				}
			} finally {
				setIsLoading(false);
			}
		},
		[email, t]
	);

	// Success state
	if (success) {
		return (
			<div data-ui="forgot-password-success" className="space-y-4">
				<Alert
					data-ui="forgot-password-success-alert"
					className="border-green-200 bg-green-50 text-green-800"
				>
					<CheckCircle2 className="h-4 w-4 text-green-600" />
					<AlertDescription>
						{t(
							'Ak existuje účet s touto emailovou adresou, odoslali sme vám pokyny na obnovenie hesla. Skontrolujte svoju emailovú schránku.'
						)}
					</AlertDescription>
				</Alert>

				<Button
					data-ui="forgot-password-back-btn"
					type="button"
					variant="outline"
					className="w-full"
					onClick={onBack}
				>
					<ArrowLeft className="mr-2 h-4 w-4" />
					{t('Späť na prihlásenie')}
				</Button>
			</div>
		);
	}

	return (
		<form data-ui="forgot-password-form" onSubmit={handleSubmit} className="space-y-4">
			{/* Description */}
			<p className="text-sm text-muted-foreground">
				{t('Zadajte svoju emailovú adresu a my vám pošleme odkaz na obnovenie hesla.')}
			</p>

			{/* Error Alert */}
			{error && (
				<Alert data-ui="forgot-password-error-alert" variant="destructive">
					<AlertCircle className="h-4 w-4" />
					<AlertDescription>{error}</AlertDescription>
				</Alert>
			)}

			{/* Email */}
			<div className="space-y-2">
				<Label htmlFor="email">{t('Emailová adresa')}</Label>
				<Input
					id="email"
					data-ui="forgot-password-email-input"
					type="email"
					value={email}
					onChange={(e) => setEmail(e.target.value)}
					placeholder="vas@email.sk"
					autoComplete="email"
					autoFocus
					disabled={isLoading}
					required
				/>
			</div>

			{/* Actions */}
			<div className="space-y-2">
				<Button
					data-ui="forgot-password-submit-btn"
					type="submit"
					className="w-full"
					disabled={isLoading || !email}
				>
					{isLoading ? (
						<>
							<Loader2 className="mr-2 h-4 w-4 animate-spin" />
							{t('Odosielam...')}
						</>
					) : (
						t('Obnoviť heslo')
					)}
				</Button>

				<Button
					type="button"
					data-ui="forgot-password-back-to-login-btn"
					variant="ghost"
					className="w-full"
					onClick={onBack}
					disabled={isLoading}
				>
					<ArrowLeft className="mr-2 h-4 w-4" />
					{t('Späť na prihlásenie')}
				</Button>
			</div>
		</form>
	);
}
